import * as Tooltip from '@radix-ui/react-tooltip'
import { Info } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Small info affordance: an ⓘ icon button that reveals explanatory content.
 *
 * Works across input types — Radix Tooltip opens on pointer hover, on keyboard
 * focus (Tab), and on touch (tapping the button focuses it). The trigger is a
 * real <button> so it is reachable and announced to assistive tech. Use for
 * short, glanceable help text (e.g. what an RBAC permission grants).
 */
export function InfoTooltip({
  content,
  label = 'More information',
  className,
}: {
  content: ReactNode
  /** Accessible label for the trigger button. */
  label?: string
  className?: string
}) {
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            aria-label={label}
            className={
              className ??
              'inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded'
            }
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            align="start"
            sideOffset={6}
            collisionPadding={8}
            className="z-[120] max-w-xs rounded-lg bg-card text-card-foreground border border-border px-3 py-2 text-sm shadow-md data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95"
          >
            {content}
            <Tooltip.Arrow className="fill-border" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}

export default InfoTooltip
